import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useBrand } from "../context/BrandContext";
import { useClubLicense } from "../context/ClubLicenseContext";
import { fetchSmartProfile, saveSmartProfile } from "../components/funtionsBD/digipingSmartService";
import { readActiveHomeRole, readStoredUser } from "../utils/homeRole";
import { resolvePlayerAgent } from "../utils/agentConfig";
import { currentAppLanguage } from "../utils/appLanguages";
import {
  LANDING_PATH_BY_ROLE,
  mapHomeRoleToTourRole,
} from "./tourIds";
import {
  START_UI_GUIDE_EVENT,
  closePingfloCompanion,
  emitUiTourSettled,
} from "./tourEvents";
import { closeTourTransientUi } from "./closeTourTransientUi";
import { arriveOnTourStep, delay } from "./tourArrive";
import { resolvePlaybook, setTourPlaybookOverlays, welcomePlaybookIdForRole } from "./playbooks";
import { fetchTourPlaybookOverlays } from "../components/funtionsBD/tourGuideService";
import { expandTourSteps, shouldUseCompactTourNav } from "./expandTourSteps";
import { executeTourAction } from "./UiActionBus";
import { armTourSpeech, cancelTourSpeech, speakTourText, warmTourVoices } from "./TourTts";
import { setPingfloTourRunning } from "./tourTarget";
import { trackTourEvent } from "./tourAnalytics";
import { restoreRealClubAfterTour } from "./clearTourDemoState";
import {
  isUiTourSettledForRole,
  markUiTourDismissedThisLogin,
  mergeUiTourState,
  readUiTourLocal,
  shouldAutoShowUiTourWelcome,
  writeUiTourLocal,
} from "./uiTourGate";
import { TourOverlay, TourWelcomeCard } from "./TourOverlay";
import { PingfloManual } from "./PingfloManual";
import {
  filterPlaybookSteps,
  needsTechnicalProfiling,
  openPlayerTechnicalProfiling,
  snoozeTechnicalProfilingPrompt,
} from "./technicalProfilingPrompt";

const PUBLIC_PATHS = [
  "/login",
  "/register",
  "/complete_profile",
  "/profiling",
  "/join",
  "/club-admin-profiling",
  "/parent-profiling",
  "/confirm-email",
  "/password-registration",
  "/set-password",
  "/book",
  "/newsletter/public",
  "/demo",
  "/flows",
  "/pilot",
  "/Verify",
  "/Termsofuse",
  "/privacy",
  "/eula",
  "/UserAgreement",
];

function isPublicPath(pathname) {
  const path = pathname || "/";
  if (path === "/") return true;
  return PUBLIC_PATHS.some((prefix) => path === prefix || path.startsWith(`${prefix}/`));
}

function hasLoggedInSession() {
  if (!localStorage.getItem("token")) return false;
  try {
    return Boolean(JSON.parse(localStorage.getItem("user") || "null")?.user_id);
  } catch {
    return false;
  }
}

function currentTourRole() {
  return mapHomeRoleToTourRole(readActiveHomeRole(readStoredUser()));
}

export default function PingfloTourHost() {
  const location = useLocation();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const { brand } = useBrand();
  const { hasFeature } = useClubLicense();
  const [uiTour, setUiTour] = useState({});
  const [phase, setPhase] = useState("idle");
  const [playbook, setPlaybook] = useState(null);
  const [stepIndex, setStepIndex] = useState(0);
  const [mode, setMode] = useState("show");
  const stepGenRef = useRef(0);
  const goToStepRef = useRef(null);
  const exitLockRef = useRef(false);
  const user = readStoredUser();
  const userId = user?.user_id;
  const [role, setRole] = useState(() => currentTourRole());
  const [smartProfile, setSmartProfile] = useState(null);
  const lang = currentAppLanguage(i18n);
  const enabled =
    hasLoggedInSession() &&
    !isPublicPath(location.pathname) &&
    brand?.show_ai_coach !== false &&
    hasFeature("pingflo_ai") !== false;

  const narratorId = useMemo(() => {
    if (role === "parent") return "pingwi";
    if (role !== "player") return "pingflo";
    return resolvePlayerAgent({ user, homeRole: "player" })?.id || "pingflo";
  }, [role, user]);

  useEffect(() => {
    const sync = () => setRole(currentTourRole());
    sync();
    window.addEventListener("smarttt-home-role-changed", sync);
    window.addEventListener("smarttt-user-updated", sync);
    window.addEventListener("storage", sync);
    return () => {
      window.removeEventListener("smarttt-home-role-changed", sync);
      window.removeEventListener("smarttt-user-updated", sync);
      window.removeEventListener("storage", sync);
    };
  }, [userId]);

  useEffect(() => {
    if (!enabled || !userId) return undefined;
    let cancelled = false;
    const local = readUiTourLocal(userId);
    setUiTour(local);
    (async () => {
      try {
        const profile = await fetchSmartProfile(null, lang);
        if (cancelled) return;
        setSmartProfile(profile || null);
        const remote = profile?.uiTour || profile?.pathMeta?.uiTour || {};
        const merged = { ...remote, ...local };
        setUiTour(merged);
        writeUiTourLocal(userId, merged);
      } catch {
        /* local only */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [enabled, userId, lang]);

  useEffect(() => {
    if (!enabled) return undefined;
    let cancelled = false;
    fetchTourPlaybookOverlays()
      .then((map) => {
        if (!cancelled) setTourPlaybookOverlays(map);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [enabled]);

  const persistRole = useCallback(
    async (patch, settledMeta) => {
      const next = mergeUiTourState(uiTour, role, patch);
      setUiTour(next);
      writeUiTourLocal(userId, next);
      emitUiTourSettled({ role, ...settledMeta });
      try {
        await saveSmartProfile({ pathMeta: { uiTour: next }, lang });
      } catch {
        /* keep local */
      }
    },
    [uiTour, role, userId, lang]
  );

  const stopRun = useCallback(() => {
    stepGenRef.current += 1;
    cancelTourSpeech();
    setPingfloTourRunning(false);
    closeTourTransientUi(null);
    setPhase("idle");
    setPlaybook(null);
    setStepIndex(0);
  }, []);

  const leaveTour = useCallback(
    ({ skipped = false } = {}) => {
      exitLockRef.current = true;
      restoreRealClubAfterTour({
        navigate,
        role,
        toProfile: skipped,
      });
      stopRun();
    },
    [navigate, role, stopRun]
  );

  useEffect(() => {
    warmTourVoices();
  }, []);

  const speakStep = useCallback(
    async (step, agentId) => {
      if (!step) return false;
      const spoken = step.body || t(step.bodyKey, step.bodyKey);
      return speakTourText(spoken, { lang, agentId, lineKey: step.bodyKey || step.id });
    },
    [t, lang]
  );

  const finishWalk = useCallback(
    async ({ skipped = false } = {}) => {
      const book = playbook;
      cancelTourSpeech();
      setPingfloTourRunning(false);
      closeTourTransientUi(null);
      trackTourEvent(skipped ? "skipped" : "completed", {
        playbook: book?.id,
        role,
        step: stepIndex,
      });
      if (role === "player" && needsTechnicalProfiling(user, smartProfile)) {
        snoozeTechnicalProfilingPrompt(userId);
      }
      if (skipped) {
        // Continue later: do not ask again. The manual stays available on demand.
        markUiTourDismissedThisLogin(userId, role);
        await persistRole(
          {
            skipped: false,
            continueLaterAt: new Date().toISOString(),
            lastDismissedAt: new Date().toISOString(),
            lastPlaybook: book?.id || null,
            technicalProfilingPending:
              role === "player" && needsTechnicalProfiling(user, smartProfile),
          },
          { skipped: true, completed: false }
        );
      } else {
        await persistRole(
          {
            completedAt: new Date().toISOString(),
            skipped: false,
            lastPlaybook: book?.id || null,
            technicalProfilingPending:
              role === "player" && needsTechnicalProfiling(user, smartProfile),
          },
          { skipped: false, completed: true }
        );
      }
      leaveTour({ skipped });
    },
    [playbook, persistRole, role, stepIndex, leaveTour, user, smartProfile, userId]
  );

  const goToStep = useCallback(
    async (index, book = playbook) => {
      if (!book) return;
      if (index >= book.steps.length) {
        finishWalk({ skipped: false });
        return;
      }
      const gen = ++stepGenRef.current;
      const step = book.steps[index];
      closePingfloCompanion();
      closeTourTransientUi(step);
      setStepIndex(index);
      trackTourEvent("step", { playbook: book.id, step: index, stepId: step.id });
      const hostId = book.hostAgentId || "pingflo";
      const agentId =
        index === 0 || String(step.id || "").includes("meet")
          ? hostId
          : step.agentId || narratorId || hostId;
      // Let dialog unmount paint before measuring the next highlight.
      await delay(90);
      if (gen !== stepGenRef.current) {
        closeTourTransientUi(null);
        return;
      }
      await arriveOnTourStep(step, { navigate });
      if (gen !== stepGenRef.current) {
        closeTourTransientUi(null);
        return;
      }
      if (!step.silent) await speakStep(step, agentId);
      if (gen !== stepGenRef.current) return;
      if (step.autoAdvanceAfterSpeech) {
        window.setTimeout(() => {
          if (gen !== stepGenRef.current) return;
          goToStepRef.current?.(index + 1, book);
        }, 320);
      }
    },
    [playbook, finishWalk, navigate, speakStep, narratorId]
  );
  goToStepRef.current = goToStep;

  const startPlaybook = useCallback(
    (book, { guideMode = "show", startIndex = 0, startStepId = null } = {}) => {
      if (!book?.steps?.length) return;
      exitLockRef.current = false;
      const expanded = {
        ...book,
        steps: expandTourSteps(filterPlaybookSteps(book.steps, { user: readStoredUser(), profile: smartProfile }), {
          compactSidebar: shouldUseCompactTourNav(),
        }),
      };
      if (!expanded.steps.length) return;
      closePingfloCompanion();
      setPlaybook(expanded);
      setMode(guideMode);
      setPhase("running");
      setPingfloTourRunning(true);
      armTourSpeech();
      trackTourEvent("started", { playbook: book.id, role: book.role, mode: guideMode });
      let idx = Math.max(0, Math.min(Number(startIndex) || 0, expanded.steps.length - 1));
      if (startStepId) {
        const found = expanded.steps.findIndex((step) => step.id === startStepId);
        if (found >= 0) idx = found;
      }
      window.setTimeout(() => {
        goToStep(idx, expanded);
      }, 0);
    },
    [goToStep, smartProfile]
  );

  const onNext = useCallback(() => {
    armTourSpeech();
    goToStep(stepIndex + 1);
  }, [goToStep, stepIndex]);

  const onDoForMe = useCallback(() => {
    armTourSpeech();
    const step = playbook?.steps?.[stepIndex];
    if (step?.doForMe) {
      executeTourAction(step.doForMe, { navigate });
      trackTourEvent("first_key_action", { playbook: playbook.id, stepId: step.id });
    }
    window.setTimeout(() => goToStep(stepIndex + 1), 280);
  }, [playbook, stepIndex, navigate, goToStep]);

  const finishPlayerTechChoice = useCallback(
    async ({ start = false } = {}) => {
      snoozeTechnicalProfilingPrompt(userId);
      cancelTourSpeech();
      setPingfloTourRunning(false);
      closeTourTransientUi(null);
      trackTourEvent("completed", {
        playbook: playbook?.id,
        role,
        step: stepIndex,
        choice: start ? "tech_start" : "tech_later",
      });
      await persistRole(
        {
          completedAt: new Date().toISOString(),
          skipped: false,
          lastPlaybook: playbook?.id || null,
          technicalProfilingPending: needsTechnicalProfiling(user, smartProfile),
        },
        { skipped: false, completed: true }
      );
      leaveTour({ skipped: false });
      if (start) {
        window.setTimeout(() => {
          openPlayerTechnicalProfiling({ t, profile: smartProfile });
        }, 420);
      }
    },
    [userId, playbook, role, stepIndex, persistRole, user, smartProfile, leaveTour, t]
  );

  useEffect(() => {
    if (phase !== "running" || mode !== "do") return undefined;
    const step = playbook?.steps?.[stepIndex];
    if (!step?.doForMe) return undefined;
    const timer = window.setTimeout(() => onDoForMe(), 450);
    return () => window.clearTimeout(timer);
  }, [phase, mode, playbook, stepIndex, onDoForMe]);

  useEffect(() => {
    if (phase !== "running") return undefined;
    const current = playbook?.steps?.[stepIndex];
    if (current?.autoAdvanceAfterSpeech) return undefined;
    const ms = Number(current?.autoAdvanceMs);
    if (!ms || ms <= 0) return undefined;
    const timer = window.setTimeout(() => onNext({ from: "auto" }), ms);
    return () => window.clearTimeout(timer);
  }, [phase, playbook, stepIndex, onNext]);

  useEffect(() => {
    if (!enabled) return undefined;
    const onStart = (event) => {
      const detail = event.detail || {};
      const tourRole = mapHomeRoleToTourRole(detail.role || currentTourRole());
      setRole(tourRole);
      const book = resolvePlaybook(detail.playbookId, { role: tourRole }) ||
        resolvePlaybook(welcomePlaybookIdForRole(tourRole), { role: tourRole });
      startPlaybook(book, {
        guideMode: detail.mode === "do" ? "do" : "show",
        startIndex: detail.startIndex,
        startStepId: detail.startStepId,
      });
    };
    window.addEventListener(START_UI_GUIDE_EVENT, onStart);
    return () => window.removeEventListener(START_UI_GUIDE_EVENT, onStart);
  }, [enabled, startPlaybook]);

  useEffect(() => {
    if (phase !== "running") return undefined;
    const onKey = (e) => {
      if (e.key !== "Escape") return;
      const current = playbook?.steps?.[stepIndex];
      if (current?.choice === "technicalProfiling") {
        finishPlayerTechChoice({ start: false });
        return;
      }
      finishWalk({ skipped: true });
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [phase, finishWalk, playbook, stepIndex, finishPlayerTechChoice]);

  useEffect(() => {
    if (exitLockRef.current) return undefined;
    if (!enabled || !userId) return undefined;
    if (phase === "welcome") {
      const landing = LANDING_PATH_BY_ROLE[role];
      if (landing && location.pathname !== landing) setPhase("idle");
      return undefined;
    }
    if (phase !== "idle") return undefined;
    if (!shouldAutoShowUiTourWelcome(role, userId)) return undefined;
    const landing = LANDING_PATH_BY_ROLE[role];
    if (!landing || location.pathname !== landing) return undefined;
    setPhase("welcome");
    return undefined;
  }, [enabled, phase, userId, uiTour, role, location.pathname]);

  // Technical profiling is opt-in (profile page, Ask Pingflo, or Digiping when
  // it needs a deeper analysis). Do not auto-prompt on home after role switch.

  useEffect(() => {
    if (phase !== "welcome") return undefined;
    const agentId = role === "parent" ? "pingwi" : "pingflo";
    const lineKey = role === "parent" ? "tour_welcome_body_parent" : "tour_welcome_body";
    const spoken =
      role === "parent"
        ? t("tour_welcome_body_parent", t("tour_welcome_body"))
        : t("tour_welcome_body");
    armTourSpeech();
    speakTourText(spoken, { lang, agentId, lineKey });
    return () => cancelTourSpeech();
  }, [phase, t, lang, role]);

  const step = playbook?.steps?.[stepIndex] || null;
  const hostAgentId = playbook?.hostAgentId || (role === "parent" ? "pingwi" : "pingflo");
  const agentForStep =
    stepIndex === 0 || !step
      ? hostAgentId
      : step.agentId || narratorId || hostAgentId;
  const demoActive = phase === "welcome" || phase === "running";
  const welcomeAgentId = role === "parent" ? "pingwi" : "pingflo";

  useEffect(() => {
    setPingfloTourRunning(enabled && demoActive);
    return () => setPingfloTourRunning(false);
  }, [enabled, demoActive]);

  useEffect(() => {
    if (typeof document === "undefined") return undefined;
    if (enabled && phase === "welcome") {
      document.documentElement.setAttribute("data-pingflo-welcome", "1");
    } else {
      document.documentElement.removeAttribute("data-pingflo-welcome");
    }
    return () => document.documentElement.removeAttribute("data-pingflo-welcome");
  }, [enabled, phase]);

  if (!enabled) return <PingfloManual role={role} />;

  return (
    <>
      <PingfloManual role={role} />
      {demoActive && phase !== "welcome" ? (
        <div className="pingflo-tour-demo-flag" role="status">
          {t("tour_demo_ribbon", "Sample club · fictitious names")}
        </div>
      ) : null}
      {phase === "welcome" ? (
        <TourWelcomeCard
          agentId={welcomeAgentId}
          role={role}
          onSkip={() => {
            if (role === "player" && needsTechnicalProfiling(user, smartProfile)) {
              snoozeTechnicalProfilingPrompt(userId);
            }
            // Continue later: do not ask again. The manual stays available on demand.
            markUiTourDismissedThisLogin(userId, role);
            persistRole(
              {
                skipped: false,
                continueLaterAt: new Date().toISOString(),
                lastDismissedAt: new Date().toISOString(),
                lastPlaybook: welcomePlaybookIdForRole(role),
                technicalProfilingPending:
                  role === "player" && needsTechnicalProfiling(user, smartProfile),
              },
              { skipped: true, completed: false }
            );
            leaveTour({ skipped: true });
          }}
          onStart={() => {
            const book = resolvePlaybook(welcomePlaybookIdForRole(role), { role });
            startPlaybook(book);
          }}
        />
      ) : null}
      {phase === "running" && step ? (
        <TourOverlay
          step={step}
          stepIndex={stepIndex}
          stepCount={playbook.steps.length}
          agentId={agentForStep}
          pagePath={location.pathname}
          onSkip={() => finishWalk({ skipped: true })}
          onNext={onNext}
          onDoForMe={onDoForMe}
          onChoiceAccept={() => finishPlayerTechChoice({ start: true })}
          onChoiceDefer={() => finishPlayerTechChoice({ start: false })}
        />
      ) : null}
    </>
  );
}
